import { useState, useEffect } from "react";
import { API } from "@/App";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Star, User, ArrowLeft, Trophy, Calendar
} from "lucide-react";

const BADGE_COLORS = {
  "Food Novice": "badge-novice",
  "Food Explorer": "badge-explorer",
  "Food Connoisseur": "badge-connoisseur",
  "Prince Foody": "badge-prince",
  "Royal Foody": "badge-royal"
};

const SharedProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/share/${userId}`);
      setProfile(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || "Profile not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background" data-testid="shared-profile-error">
        <header className="border-b border-border bg-card/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-muted-foreground">
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-primary fill-primary" />
                <span className="font-semibold gradient-text">Étoile</span>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-16 text-center">
          <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Profile Not Available</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-primary text-primary-foreground">
            Go to Homepage
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shared-profile-page">
      <header className="border-b border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Profile Card */}
        <Card className="bg-card border-border mb-6">
          <CardContent className="p-8 text-center">
            {profile.profile.picture ? (
              <img src={profile.profile.picture} alt="" className="w-24 h-24 rounded-full mx-auto mb-4" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary/20 mx-auto mb-4 flex items-center justify-center">
                <User className="w-12 h-12 text-primary" />
              </div>
            )}
            <h1 className="text-2xl font-bold mb-2">{profile.profile.name}</h1>
            <p className={`text-lg ${BADGE_COLORS[profile.profile.badge_tier]} mb-4`}>
              {profile.profile.badge_tier}
            </p>
            <div className="flex items-center justify-center gap-2 text-3xl font-bold gradient-text">
              <Star className="w-8 h-8 text-primary fill-primary" />
              {profile.profile.total_stars}
              <span className="text-lg font-normal text-muted-foreground">stars</span>
            </div>
          </CardContent>
        </Card>

        {/* Recent Visits */}
        {profile.recent_visits?.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Recent Visits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {profile.recent_visits.map((visit) => (
                  <div key={`visit-${visit.restaurant_id || visit.restaurant_name}-${visit.visit_date}`} className="flex items-center justify-between p-3 bg-muted/20 rounded">
                    <div>
                      <p className="font-medium">{visit.restaurant_name}</p>
                      <p className="text-xs text-muted-foreground">{visit.visit_date}</p>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(visit.stars)].map((_, j) => (
                        <Star key={`star-${visit.restaurant_name}-${j}`} className="w-4 h-4 text-primary fill-primary" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Join CTA */}
        <div className="text-center mt-8">
          <p className="text-muted-foreground mb-4">
            Track your own Michelin dining journey
          </p>
          <Button onClick={() => navigate('/auth')} className="bg-primary text-primary-foreground">
            Join Étoile
          </Button>
        </div>
      </main>
    </div>
  );
};

export default SharedProfile;
